import { assign, sendParent, setup } from 'xstate';

import { ApiError } from '@/modules/common/api/errors/api-error';
import { getNotPassedOnboardingRoute } from '@/modules/onboarding/common/api/onboarding-flow.api';
import { AuthenticatedRoutes, OnboardingRoutes } from '@/router/routes';
import { GLOBAL_REDIRECT_BACKEND_CODES } from '@/utils/axios';
import { logger } from '@/utils/logger/logger';

export enum OnboardingSteps {
  Organization = 'organization',
  Integrations = 'integrations',
  Team = 'team',
  Clients = 'clients',
  Projects = 'projects',
  Complete = 'complete',
}

const stepToRouteMap: Record<OnboardingSteps, OnboardingRoutes | null> = {
  [OnboardingSteps.Organization]: OnboardingRoutes.OnboardingOrganization,
  [OnboardingSteps.Integrations]: OnboardingRoutes.OnboardingIntegrations,
  [OnboardingSteps.Team]: OnboardingRoutes.OnboardingTeam,
  [OnboardingSteps.Clients]: OnboardingRoutes.OnboardingClients,
  [OnboardingSteps.Projects]: OnboardingRoutes.OnboardingProjects,
  [OnboardingSteps.Complete]: null,
};

interface StepTransition {
  guard: ({ event }: { event: any }) => boolean;
  target: string;
  actions: any;
}

function createStepTransition(step: OnboardingSteps, route: OnboardingRoutes): StepTransition {
  return {
    guard: ({ event }: any) => event.targetRoute === route,
    target: `.${route}`,
    actions: assign({
      isLoading: false,
      currentStep: step,
    }),
  };
}

// Generate transitions excluding the "Complete" step handled separately.
const stepTransitions = Object.entries(stepToRouteMap)
  .filter(([step, route]) => step !== OnboardingSteps.Complete && route !== null)
  .map(([step, route]) => createStepTransition(step as OnboardingSteps, route!));

export interface OnboardingContext {
  intendedRoute: string | null;
  currentStep: OnboardingSteps;
  isLoading: boolean;
  error: string | null;
  isNonOnboardingError: boolean;
}

export type OnboardingEvents =
  | { type: 'BACK' }
  | { type: 'NEXT' }
  | { type: 'COMPLETE' }
  | { type: 'STARTED_FETCHING_NEXT_STEP' }
  | {
      type: 'FETCH_NEXT_STEP_SUCCESS';
      targetRoute:
        | OnboardingRoutes
        | null
        | AuthenticatedRoutes.OnboardingIntegrationsOAuthCallback;
    }
  | { type: 'FETCH_NEXT_STEP_ERROR'; error: string; isNonOnboardingError: boolean };

export interface OnboardingInput {
  intendedRoute: string | null;
}

const TAG = 'OnboardingMachine:';

export const onboardingMachine = setup({
  types: {
    context: {} as OnboardingContext,
    events: {} as OnboardingEvents,
    input: {} as OnboardingInput,
  },
  actions: {
    fetchNextStep: async ({ self, context }) => {
      if (context.intendedRoute == AuthenticatedRoutes.OnboardingIntegrationsOAuthCallback) {
        logger.debug(TAG, 'Intended route is OAuth callback, skipping fetch');
        self.send({
          type: 'FETCH_NEXT_STEP_SUCCESS',
          targetRoute: AuthenticatedRoutes.OnboardingIntegrationsOAuthCallback,
        });
        return;
      }

      // If the user lands directly on a global redirect route (e.g. /billing/expired),
      // skip the API call entirely — the page renders standalone without onboarding flow.
      const globalRedirectRoutes = Object.values(GLOBAL_REDIRECT_BACKEND_CODES);
      if (globalRedirectRoutes.includes(context.intendedRoute as AuthenticatedRoutes)) {
        logger.debug(TAG, 'Intended route is a global redirect route, skipping fetch');
        self.send({ type: 'COMPLETE' });
        return;
      }

      try {
        self.send({ type: 'STARTED_FETCHING_NEXT_STEP' });

        const notPassedRoute = await getNotPassedOnboardingRoute();

        if (notPassedRoute != null && context.intendedRoute) {
          logger.debug(TAG, 'Processing intended route:', context.intendedRoute);
          const onboardingRoutes = Object.values(OnboardingRoutes);

          const intendedRouteIndex = onboardingRoutes.findIndex(
            route => route === context.intendedRoute
          );
          const notPassedRouteIndex = onboardingRoutes.findIndex(route => route === notPassedRoute);

          const intendedRoute =
            intendedRouteIndex == -1 ? null : onboardingRoutes[intendedRouteIndex];

          if (
            intendedRoute &&
            intendedRoute != OnboardingRoutes.OnboardingError &&
            // Allow manual navigation to the passed steps only
            intendedRouteIndex <= notPassedRouteIndex
          ) {
            logger.debug(TAG, 'Using intended route:', intendedRoute);
            self.send({
              type: 'FETCH_NEXT_STEP_SUCCESS',
              targetRoute: intendedRoute,
            });
            return;
          }
        }

        logger.debug(TAG, 'Fetched next onboarding route:', notPassedRoute);
        self.send({
          type: 'FETCH_NEXT_STEP_SUCCESS',
          targetRoute: notPassedRoute,
        });
      } catch (error) {
        logger.error(TAG, 'Failed to fetch next onboarding step:', error);

        // Some errors are handled globally by the axios interceptor (see GLOBAL_REDIRECT_BACKEND_CODES
        // in axios.ts), which navigates to a dedicated full-page route. Suppress machine error
        // handling for those to prevent AuthenticatedError from flashing before navigation completes.
        if (
          error instanceof ApiError &&
          error.backendCode !== undefined &&
          error.backendCode in GLOBAL_REDIRECT_BACKEND_CODES
        ) {
          return;
        }

        // 401 is also handled globally by the axios interceptor: it shows a
        // "session expired" toast and dispatches LOGOUT_REQUESTED, which tears
        // down the authenticated tree (including this machine). Suppress the
        // machine's own error state so AuthenticatedError doesn't flash before
        // the logout transition completes.
        if (error instanceof ApiError && error.httpStatus === 401) {
          return;
        }

        // On retry (error already set), preserve the existing flag.
        // On first failure, determine from intendedRoute: if null or not an onboarding step,
        // this is a non-onboarding user.
        let isNonOnboardingError = context.isNonOnboardingError;
        if (context.error === null) {
          const onboardingStepRoutes = Object.values(OnboardingRoutes).filter(
            route => route !== OnboardingRoutes.OnboardingError
          ) as string[];
          isNonOnboardingError = !onboardingStepRoutes.includes(context.intendedRoute ?? '');
        }

        self.send({
          type: 'FETCH_NEXT_STEP_ERROR',
          error: error instanceof Error ? error.message : 'Unknown error',
          isNonOnboardingError,
        });
      }
    },
  },
}).createMachine({
  id: 'onboardingMachine',
  initial: 'loading',
  context: ({ input }) => ({
    intendedRoute: input.intendedRoute,
    currentStep: OnboardingSteps.Organization,
    isLoading: false,
    error: null,
    isNonOnboardingError: false,
  }),
  states: {
    loading: {
      entry: 'fetchNextStep',
      exit: sendParent({ type: 'INITIALIZED_ONBOARDING' }),
    },
    [AuthenticatedRoutes.OnboardingIntegrationsOAuthCallback]: {},
    [OnboardingRoutes.OnboardingError]: {},
    [OnboardingRoutes.OnboardingOrganization]: {
      on: {
        NEXT: {
          target: OnboardingRoutes.OnboardingIntegrations,
          actions: assign({
            currentStep: OnboardingSteps.Integrations,
          }),
        },
      },
    },
    [OnboardingRoutes.OnboardingIntegrations]: {
      on: {
        BACK: {
          target: OnboardingRoutes.OnboardingOrganization,
          actions: assign({
            currentStep: OnboardingSteps.Organization,
          }),
        },
        NEXT: {
          target: OnboardingRoutes.OnboardingTeam,
          actions: assign({
            currentStep: OnboardingSteps.Team,
          }),
        },
      },
    },
    [OnboardingRoutes.OnboardingTeam]: {
      on: {
        BACK: {
          target: OnboardingRoutes.OnboardingIntegrations,
          actions: assign({
            currentStep: OnboardingSteps.Integrations,
          }),
        },
        NEXT: {
          target: OnboardingRoutes.OnboardingClients,
          actions: assign({
            currentStep: OnboardingSteps.Clients,
          }),
        },
      },
    },
    [OnboardingRoutes.OnboardingClients]: {
      on: {
        BACK: {
          target: OnboardingRoutes.OnboardingTeam,
          actions: assign({
            currentStep: OnboardingSteps.Team,
          }),
        },
        NEXT: {
          target: OnboardingRoutes.OnboardingProjects,
          actions: assign({
            currentStep: OnboardingSteps.Projects,
          }),
        },
      },
    },
    [OnboardingRoutes.OnboardingProjects]: {
      on: {
        BACK: {
          target: OnboardingRoutes.OnboardingClients,
          actions: assign({
            currentStep: OnboardingSteps.Clients,
          }),
        },
        NEXT: {
          target: OnboardingRoutes.OnboardingCompletion,
          actions: assign({
            currentStep: OnboardingSteps.Complete,
          }),
        },
      },
    },
    [OnboardingRoutes.OnboardingCompletion]: {},
    [OnboardingSteps.Complete]: {
      type: 'final',
    },
  },
  on: {
    COMPLETE: {
      target: `.${OnboardingSteps.Complete}`,
    },
    NEXT: {
      actions: [
        assign({
          intendedRoute: null,
        }),
        { type: 'fetchNextStep' },
      ],
    },
    STARTED_FETCHING_NEXT_STEP: {
      actions: assign({
        isLoading: true,
        error: null,
      }),
    },
    FETCH_NEXT_STEP_SUCCESS: [
      {
        // If nextStep is null, complete onboarding.
        guard: ({ event }) => event.targetRoute === null,
        target: `.${OnboardingSteps.Complete}`,
        actions: assign({
          isLoading: false,
          currentStep: OnboardingSteps.Complete,
        }),
      },
      ...stepTransitions,
      {
        guard: ({ event }) =>
          event.targetRoute === AuthenticatedRoutes.OnboardingIntegrationsOAuthCallback,
        target: `.${AuthenticatedRoutes.OnboardingIntegrationsOAuthCallback}`,
        actions: assign({
          isLoading: false,
        }),
      },
      {
        // Default case if next step is not recognized.
        actions: assign({
          isLoading: false,
          error: 'Unknown next step',
        }),
      },
    ],
    FETCH_NEXT_STEP_ERROR: {
      actions: assign({
        isLoading: false,
        error: ({ event }) => event.error,
        isNonOnboardingError: ({ event }) => event.isNonOnboardingError,
      }),
      target: `.${OnboardingRoutes.OnboardingError}`,
    },
  },
});
